import { Button, Separator } from "@monbits/ui";
import { AlertTriangle, FileText, Loader2, XCircle } from "lucide-react";
import type * as React from "react";
import CopyButton from "src/components/copy-button/copy-button";
import { RiskLevelBadge } from "src/components/risk-level-badge";
import { StatusBadge } from "src/components/status-badge";
import type {
	AMLCheck,
	AMLCheckStatus,
	AMLTransaction,
	AMLTransactionStatus,
} from "src/types";

const formatStatusLabel = (status: AMLTransactionStatus) => {
	switch (status) {
		case "pending":
			return "Pending";
		case "confirmed":
			return "Confirmed";
		case "on_hold":
		case "onHold":
			return "On Hold";
		case "in_progress":
		case "inProgress":
			return "In Progress";
		case "completed":
			return "Completed";
		case "failed":
			return "Failed";
		case "cancelled":
			return "Cancelled";
		default:
			return status;
	}
};

const formatAMLStatusLabel = (status: AMLCheckStatus) => {
	switch (status) {
		case "pending":
			return "Pending";
		case "approved":
			return "Approved";
		case "flagged":
			return "Flagged";
		case "rejected":
			return "Rejected";
		default:
			return status;
	}
};

const resolveStatusBadgeType = (status: AMLTransactionStatus) => {
	switch (status) {
		case "pending":
			return "default";
		case "confirmed":
			return "info";
		case "on_hold":
		case "onHold":
			return "warning";
		case "in_progress":
		case "inProgress":
			return "info";
		case "completed":
			return "success";
		case "failed":
			return "error";
		case "cancelled":
			return "error";
		default:
			return "default";
	}
};

const resolveAMLStatusBadgeType = (status: AMLCheckStatus) => {
	switch (status) {
		case "pending":
			return "default";
		case "approved":
			return "success";
		case "flagged":
			return "warning";
		case "rejected":
			return "error";
		default:
			return "default";
	}
};

interface AMLDrawerContentProps {
	transaction: AMLTransaction;
	amlCheck?: AMLCheck | null;
	isLoadingMetadata?: boolean;
}

const AMLDrawerContent: React.FC<AMLDrawerContentProps> = ({
	transaction,
	amlCheck,
	isLoadingMetadata = false,
}) => {
	// Use provided amlCheck (with full metadata) or fall back to transaction's amlCheck
	const check = amlCheck ?? transaction.amlCheck;
	const metadata = check?.metadata;

	return (
		<div className="px-6 pb-6 space-y-6">
			{/* AML Error Alert */}
			{check?.errorMessage && (
				<div className="flex items-start gap-3 p-4 border rounded-lg bg-red-50 dark:bg-red-950/30 overflow-hidden">
					<XCircle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
					<div className="flex-1 min-w-0 space-y-1">
						<div className="font-medium text-sm text-red-900 dark:text-red-200">
							AML Check Error
						</div>
						<div className="text-sm text-red-700 dark:text-red-400 break-all overflow-wrap-anywhere">
							{check.errorMessage}
						</div>
					</div>
				</div>
			)}

			{/* Blacklist Warning */}
			{metadata?.has_blacklist_flag && (
				<div className="flex items-start gap-3 p-4 border rounded-lg bg-red-100 dark:bg-red-950/50 border-red-300 dark:border-red-800">
					<AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
					<div className="flex-1 min-w-0 space-y-1">
						<div className="font-medium text-sm text-red-900 dark:text-red-200">
							Blacklist Connection Detected
						</div>
						<div className="text-sm text-red-700 dark:text-red-400">
							This address has connections to known blacklisted entities.
						</div>
					</div>
				</div>
			)}

			{/* Transaction Summary */}
			<div className="space-y-3">
				<h3 className="text-sm font-semibold">Transaction Summary</h3>
				<div className="space-y-3">
					<div>
						<div className="text-xs font-medium text-muted-foreground mb-1">
							Status
						</div>
						<StatusBadge type={resolveStatusBadgeType(transaction.status)}>
							{formatStatusLabel(transaction.status)}
						</StatusBadge>
					</div>
					<div>
						<div className="text-xs font-medium text-muted-foreground mb-1">
							Type
						</div>
						<div className="text-sm capitalize">{transaction.type}</div>
					</div>
					<div>
						<div className="text-xs font-medium text-muted-foreground mb-1">
							Blockchain
						</div>
						<div className="text-sm font-medium uppercase">
							{transaction.blockchain}
						</div>
					</div>
					<div>
						<div className="text-xs font-medium text-muted-foreground mb-1">
							Amount
						</div>
						<div className="font-mono text-sm font-medium">
							{parseFloat(transaction.amount).toFixed(6)} {transaction.currency}
						</div>
					</div>
					<div>
						<div className="text-xs font-medium text-muted-foreground mb-1">
							Created
						</div>
						<div className="text-sm">
							{new Date(transaction.createdAt).toLocaleString()}
						</div>
					</div>
				</div>
			</div>

			<Separator />

			{/* Addresses */}
			<div className="space-y-3">
				<h3 className="text-sm font-semibold">Addresses</h3>
				<div className="space-y-3">
					{transaction.hash && (
						<div>
							<div className="text-xs font-medium text-muted-foreground mb-1">
								Transaction Hash
							</div>
							<div className="flex items-center gap-2">
								<div className="font-mono text-xs break-all flex-1">
									{transaction.hash}
								</div>
								<CopyButton value={transaction.hash} label="TX Hash" />
							</div>
						</div>
					)}
					{transaction.senderAddress && (
						<div>
							<div className="text-xs font-medium text-muted-foreground mb-1">
								Sender Address
							</div>
							<div className="flex items-center gap-2">
								<div className="font-mono text-xs break-all flex-1">
									{transaction.senderAddress}
								</div>
								<CopyButton value={transaction.senderAddress} label="Sender" />
							</div>
						</div>
					)}
					<div>
						<div className="text-xs font-medium text-muted-foreground mb-1">
							Recipient Address
						</div>
						<div className="flex items-center gap-2">
							<div className="font-mono text-xs break-all flex-1">
								{transaction.recipientAddress}
							</div>
							<CopyButton
								value={transaction.recipientAddress}
								label="Recipient"
							/>
						</div>
					</div>
				</div>
			</div>

			{/* Merchant Info */}
			{transaction.merchantName && (
				<>
					<Separator />
					<div className="space-y-3">
						<h3 className="text-sm font-semibold">Merchant</h3>
						<div className="space-y-3">
							<div>
								<div className="text-xs font-medium text-muted-foreground mb-1">
									Name
								</div>
								<div className="text-sm">{transaction.merchantName}</div>
							</div>
							{transaction.userEmail && (
								<div>
									<div className="text-xs font-medium text-muted-foreground mb-1">
										Owner Email
									</div>
									<div className="text-sm">{transaction.userEmail}</div>
								</div>
							)}
							{transaction.customerEmail && (
								<div>
									<div className="text-xs font-medium text-muted-foreground mb-1">
										Customer Email
									</div>
									<div className="text-sm">{transaction.customerEmail}</div>
								</div>
							)}
						</div>
					</div>
				</>
			)}

			{/* AML Risk Assessment */}
			{check && (
				<>
					<Separator />
					<div className="space-y-3">
						<h3 className="text-sm font-semibold">AML Risk Assessment</h3>
						<div className="space-y-3">
							<div>
								<div className="text-xs font-medium text-muted-foreground mb-1">
									AML Status
								</div>
								<StatusBadge type={resolveAMLStatusBadgeType(check.status)}>
									{formatAMLStatusLabel(check.status)}
								</StatusBadge>
							</div>
							<div>
								<div className="text-xs font-medium text-muted-foreground mb-1">
									Risk Level
								</div>
								<RiskLevelBadge riskLevel={check.riskLevel} />
							</div>
							<div>
								<div className="text-xs font-medium text-muted-foreground mb-1">
									Risk Score
								</div>
								<div className="text-sm font-semibold">{check.riskScore}</div>
							</div>
							<div>
								<div className="text-xs font-medium text-muted-foreground mb-1">
									Provider
								</div>
								<div className="text-sm">{check.provider}</div>
							</div>
							<div>
								<div className="text-xs font-medium text-muted-foreground mb-1">
									Check Date
								</div>
								<div className="text-sm">
									{new Date(check.createdAt).toLocaleString()}
								</div>
							</div>

							{/* Risk Signals Breakdown */}
							{isLoadingMetadata ? (
								<div className="flex items-center gap-2 pt-2 text-sm text-muted-foreground">
									<Loader2 className="h-4 w-4 animate-spin" />
									Loading detailed risk analysis...
								</div>
							) : metadata?.signals &&
								Object.keys(metadata.signals).length > 0 ? (
								<div className="space-y-2 pt-2">
									<div className="text-xs font-medium text-muted-foreground">
										Risk Signals
									</div>
									<div className="space-y-2">
										{Object.entries(metadata.signals)
											.sort(([, a], [, b]) => (b as number) - (a as number))
											.map(([category, percentage]) => (
												<div key={category} className="space-y-1">
													<div className="flex justify-between text-xs">
														<span className="capitalize">
															{category.replace(/_/g, " ")}
														</span>
														<span className="font-medium">
															{((percentage as number) * 100).toFixed(1)}%
														</span>
													</div>
													<div className="h-1.5 bg-muted rounded-full overflow-hidden">
														<div
															className="h-full bg-primary rounded-full transition-all"
															style={{
																width: `${Math.min((percentage as number) * 100, 100)}%`,
															}}
														/>
													</div>
												</div>
											))}
									</div>
								</div>
							) : null}

							{/* Counterparty Info */}
							{metadata?.counterparty?.name && (
								<div className="pt-2">
									<div className="text-xs font-medium text-muted-foreground mb-1">
										Counterparty
									</div>
									<div className="text-sm">
										{metadata.counterparty.name}
										{metadata.counterparty.type && (
											<span className="text-muted-foreground ml-1">
												({metadata.counterparty.type})
											</span>
										)}
									</div>
								</div>
							)}

							{/* PDF Report */}
							{metadata?.pdf_report && (
								<div className="pt-2">
									<div className="text-xs font-medium text-muted-foreground mb-2">
										AML Report
									</div>
									<Button
										variant="outline"
										size="sm"
										onClick={() => window.open(metadata.pdf_report, "_blank")}
										className="gap-2"
									>
										<FileText className="h-4 w-4" />
										View PDF Report
									</Button>
								</div>
							)}
						</div>
					</div>
				</>
			)}

			{/* Order Info */}
			{(transaction.orderUuid || transaction.orderMerchantId) && (
				<>
					<Separator />
					<div className="space-y-3">
						<h3 className="text-sm font-semibold">Order Details</h3>
						<div className="space-y-3">
							{transaction.orderUuid && (
								<div>
									<div className="text-xs font-medium text-muted-foreground mb-1">
										Order UUID
									</div>
									<div className="flex items-center gap-2">
										<div className="font-mono text-xs">
											{transaction.orderUuid}
										</div>
										<CopyButton
											value={transaction.orderUuid}
											label="Order UUID"
										/>
									</div>
								</div>
							)}
							{transaction.orderMerchantId && (
								<div>
									<div className="text-xs font-medium text-muted-foreground mb-1">
										Merchant Order ID
									</div>
									<div className="text-sm">{transaction.orderMerchantId}</div>
								</div>
							)}
						</div>
					</div>
				</>
			)}

			{/* Technical Details */}
			<Separator />
			<div className="space-y-3">
				<h3 className="text-sm font-semibold">Technical Details</h3>
				<div className="space-y-3">
					<div>
						<div className="text-xs font-medium text-muted-foreground mb-1">
							Transaction ID
						</div>
						<div className="font-mono text-sm">#{transaction.id}</div>
					</div>
					{check && (
						<div>
							<div className="text-xs font-medium text-muted-foreground mb-1">
								AML Check ID
							</div>
							<div className="font-mono text-sm">#{check.id}</div>
						</div>
					)}
					<div>
						<div className="text-xs font-medium text-muted-foreground mb-1">
							Last Updated
						</div>
						<div className="text-sm">
							{new Date(transaction.updatedAt).toLocaleString()}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export { AMLDrawerContent };
